import { ICompileService, IHttpService, IPromise } from "angular";
import { RecordDataSource } from "../../../../../records/grids";
import {
  CellDoubleClickedEventHandlerArgs,
  DoubleClickHandlerService,
  vsName as DoubleClickHandlerServiceName,
} from "../../../../../records/grids/cells/doubleClickHandlerService";
import { FilterProvider } from "../../../../../records/grids/columns/filters/FilterProvider";
import { LookupFilter } from "../../../../../records/grids/columns/filters/subFilters/LookupFilter";
import { OptionsFilter } from "../../../../../records/grids/columns/filters/subFilters/OptionsFilter";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
  IGridSettings,
} from "../../../../../records/grids/settings/gridSettingsService";
import {
  booleanValueGetterFactory,
  dateValueGetter,
  lookupValueGetterFactory,
} from "../../../../../records/grids/cells/valueGetters";
import { PatchObject, Record } from "../../../../../records/server/crud";
import { IQuery } from "../../../../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../../records/server/serverUtility";
import fieldHelpers from "../../../../../ui-formly/fieldHelpers";
import { ColDef } from "../../../../../records/grids/columns/colDef";
import { ProgressivePermissions } from "../../../../permissions";
import { machineConfigurationCellClass } from "../../../../grids/cellHelpers";
import { AngularGridTemplates } from "../../../../../utils/AngularGrid/templates";

const gridId = Symbol("progressiveDefinitionsMachines");

const manufacturerUrl = "/api/machines/manufacturers";

export class DefinitionsDetailMachineViewController {
  static $inject = [
    "progressiveDefinition",
    "$compile",
    ServerUtilityName,
    DoubleClickHandlerServiceName,
    GridSettingsServiceName,
    "date",
    "angularGridTemplates",
  ];

  readonly columns: ColDef[];
  readonly gridDatasource: RecordDataSource;
  readonly gridSettings: IGridSettings;
  private readonly apiUrl: string;
  activeConfigurationsOnly: boolean = true;
  defaultQuery: IQuery = { Active: this.activeConfigurationsOnly };

  constructor(
    progressiveDefinition: Record,
    $compile: ICompileService,
    private readonly serverUtility: ServerUtility,
    doubleClickHandlerService: DoubleClickHandlerService,
    gridSettingsService: GridSettingsService,
    private readonly date: Date,
    angularGridTemplates: AngularGridTemplates
  ) {
    this.apiUrl = `/api/machines/progressives/ProgressiveDefinition/${progressiveDefinition.Id}/Machines`;
    this.gridDatasource = new RecordDataSource(serverUtility, this.apiUrl);
    this.columns = [
      {
        colId: "detail",
        headerName: "Info",
        width: 20,
        template: angularGridTemplates.detailUrl(
          "main.machines.machine.configurations({ machineId: data.MachineId})"
        ),
        editable: false,
        suppressMenu: true,
        suppressSorting: true,
      },
      {
        field: "Id",
        headerName: "Id",
        filter: new FilterProvider("Id", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
        hide: true,
      },
      {
        field: "MachineConfigurationDate",
        headerName: "Configuration Date",
        filter: new FilterProvider("MachineConfigurationDate", $compile)
          .defaultDateFilters()
          .getGridFilterFactory(),
        valueGetter: dateValueGetter,
      },
      {
        field: "MachineConfigurationCurrentConfiguration",
        headerName: "Current Configuration",
        valueGetter: booleanValueGetterFactory(),
        filter: new FilterProvider(
          "MachineConfigurationCurrentConfiguration",
          $compile
        )
          .defaultBooleanFilters()
          .getGridFilterFactory(),
      },
      {
        field: "MachineConfigurationStatus",
        headerName: "Configuration Status",
        filter: new FilterProvider("MachineConfigurationStatus", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              includeNullValue: true,
              displayName: "=",
              options: [
                { display: "Floor", value: "Floor" },
                { display: "Decommissioned", value: "Decommissioned" },
                { display: "Warehoused", value: "Warehoused" },
              ],
              queryFieldNames: ["MachineConfigurationStatus"],
            })
          )
          .getGridFilterFactory(),
      },
      {
        field: "MachineConfigurationChangeType",
        headerName: "Configuration Change Type",
        valueGetter: lookupValueGetterFactory({
          lookup: {
            NewMachine: "New Machine",
          },
        }),
        filter: new FilterProvider("MachineConfigurationChangeType", $compile)
          .addFilter(
            "=",
            new OptionsFilter({
              includeNullValue: true,
              displayName: "=",
              options: [
                { display: "Move", value: "Move" },
                { display: "Conversion", value: "Conversion" },
                { display: "New Machine", value: "NewMachine" },
                { display: "Unknown", value: "Unknown" },
                { display: "Decommission", value: "Decommission" },
                { display: "Warehouse", value: "Warehouse" },
              ],
              queryFieldNames: ["MachineConfigurationChangeType"],
            })
          )
          .getGridFilterFactory(),
      },
      {
        field: "ManufacturerName",
        headerName: "Manufacturer",
        filter: new FilterProvider("ManufacturerName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["ManufacturerId"],
              serverQuery: (search) =>
                serverUtility.lookupQueryLegacy({
                  url: manufacturerUrl,
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      },
      {
        headerName: "Serial Number",
        field: "MachineSerialNumber",
        filter: new FilterProvider("MachineSerialNumber", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      },
      {
        headerName: "Slot Number",
        field: "MachineConfigurationSlotNumber",
        filter: new FilterProvider("MachineConfigurationSlotNumber", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      },
      {
        headerName: "Section",
        field: "MachineConfigurationSection",
        filter: new FilterProvider("MachineConfigurationSection", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
        hide: true,
      },
      {
        headerName: "Bank",
        field: "MachineConfigurationBank",
        filter: new FilterProvider("MachineConfigurationBank", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
        hide: true,
      },
      {
        headerName: "Slot",
        field: "MachineConfigurationSlot",
        filter: new FilterProvider("MachineConfigurationSlot", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
        hide: true,
      },
      {
        headerName: "Location",
        field: "MachineConfigurationLocation",
        filter: new FilterProvider("MachineConfigurationLocation", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      },
      {
        headerName: "Game Name",
        field: "MachineConfigurationGameName",
        filter: new FilterProvider("MachineConfigurationGameName", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      },
      {
        headerName: "Active",
        field: "Active",
        filter: new FilterProvider("Active", $compile)
          .defaultBooleanFilters()
          .getGridFilterFactory(),
        valueGetter: booleanValueGetterFactory(),
      },
      {
        headerName: "Notes",
        field: "Notes",
        filter: new FilterProvider("Notes", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) => this.fetchRecord(args),
          submit: (patchObject) => this.patchRecord(patchObject),
          patchField: {
            formField: fieldHelpers.textAreaField({
              key: "Notes",
              name: "Notes",
              label: "Notes",
              rows: 5,
              required: false,
            }),
          },
        }),
      },
    ];

    const cssClassFunction = machineConfigurationCellClass(
      date,
      "MachineConfigurationCurrentConfiguration",
      "MachineConfigurationDate"
    );
    this.columns.forEach((c) => {
      c.cellClass = cssClassFunction;
    });

    this.gridSettings = gridSettingsService.getGridSettings(gridId);
  }

  activeConfigurationsOnlyChanged(activeConfigurationsOnly: boolean) {
    if (activeConfigurationsOnly) {
      this.defaultQuery.Active = true;
    } else {
      this.defaultQuery.Active = undefined;
    }
  }

  private patchRecord(patchObject: PatchObject): IPromise<unknown> {
    return this.serverUtility.patch({
      url: `/api/machines/progressives/ProgressiveDefinitionMachine`,
      patchObject,
    });
  }

  private fetchRecord(
    args: CellDoubleClickedEventHandlerArgs
  ): IPromise<Record | undefined> {
    return this.serverUtility.querySingle({
      url: this.apiUrl,
      query: { Id: args.data.Id },
    });
  }
}

/*
    public bool Active { get; set; }
    public string Notes { get; set; }
*/
